import { assertNever } from '../utils/assertNever';
import { ImportJobStatus, RecipientSource, WorkOrderAction, WorkOrderStatus, WorkOrderType } from '../generated/enums.gen';
import { WORK_ORDER_OPT_OUT_LINE } from '../generated/constants.gen';

/** Human labels for each Work Order type. */
export const WORK_ORDER_TYPE_LABELS: Record<WorkOrderType, string> = {
  [WorkOrderType.TextSend]: 'Text send',
  [WorkOrderType.VoterData]: 'Voter data',
};

/** Who is looking at the Work Orders area, as far as its two gates care. */
export interface WorkOrderViewer {
  isPlatformAdmin: boolean;
  /** The campaign owner or a member on the FullAdmin preset. */
  isFullAdmin: boolean;
  /** The campaign's per-campaign Work Orders flag — gates Text send only (ADR-090). */
  workOrdersEnabled: boolean;
}

/**
 * Whether the viewer may reach the Work Orders area at all (mirrors the API's area gate): a
 * platform admin or any full admin, whatever the campaign's Work Orders flag.
 */
export function canReachWorkOrders(viewer: WorkOrderViewer): boolean {
  return viewer.isPlatformAdmin || viewer.isFullAdmin;
}

/**
 * Whether the viewer may create or act on an order of `type` (mirrors the API's per-type gate).
 * A full admin needs the campaign's Work Orders flag for a Text send, but not for Voter data.
 */
export function canActOnWorkOrderType(type: WorkOrderType, viewer: WorkOrderViewer): boolean {
  if (viewer.isPlatformAdmin) return true;
  if (!viewer.isFullAdmin) return false;
  switch (type) {
    case WorkOrderType.TextSend:
      return viewer.workOrdersEnabled;
    case WorkOrderType.VoterData:
      return true;
    default:
      return assertNever(type);
  }
}

/**
 * A Text send's message as the vendor sends it: body, Paid-for disclaimer and Opt-out line, one per
 * line. The same assembly the API returns as `details.assembledMessage` and holds to
 * `WORK_ORDER_MAX_MESSAGE_CHARS`.
 */
export function assembleTextSendMessage(body: string, paidForDisclaimer: string): string {
  return `${body}\n${paidForDisclaimer}\n${WORK_ORDER_OPT_OUT_LINE}`;
}

/**
 * A campaign voter reachable by text — a voter with a non-empty cell phone. The exact
 * column set the invoice freeze (#458) serializes to CSV and the vendor handoff (#462)
 * ships (`first_name,last_name,cell_phone`), so it is defined once here. Values are
 * exactly as stored (no E.164 normalization).
 */
export interface ContactableRecipient {
  firstName: string;
  lastName: string;
  cellPhone: string;
}

/**
 * What a Work Order is *awaiting* — the jobs-list grouping bucket, derived client-side
 * from the raw status (the list endpoint returns only the status). Ordered as the
 * pipeline flows so a bucketed list reads top-to-bottom.
 */
export enum WorkOrderBucket {
  AwaitingPayment = 'AwaitingPayment',
  AwaitingHandoff = 'AwaitingHandoff',
  AwaitingManualSettlement = 'AwaitingManualSettlement',
  AwaitingCompletion = 'AwaitingCompletion',
  Done = 'Done',
}

/** Human labels for each jobs-list bucket. */
export const WORK_ORDER_BUCKET_LABELS: Record<WorkOrderBucket, string> = {
  [WorkOrderBucket.AwaitingPayment]: 'Awaiting payment',
  [WorkOrderBucket.AwaitingHandoff]: 'Awaiting handoff',
  [WorkOrderBucket.AwaitingManualSettlement]: 'Awaiting manual settlement',
  [WorkOrderBucket.AwaitingCompletion]: 'Awaiting completion',
  [WorkOrderBucket.Done]: 'Done',
};

/** Render order for the jobs-list buckets — top-to-bottom along the pipeline. */
export const WORK_ORDER_BUCKET_ORDER: readonly WorkOrderBucket[] = [
  WorkOrderBucket.AwaitingPayment,
  WorkOrderBucket.AwaitingHandoff,
  WorkOrderBucket.AwaitingManualSettlement,
  WorkOrderBucket.AwaitingCompletion,
  WorkOrderBucket.Done,
];

/**
 * Maps a Work Order status to the jobs-list bucket it is *awaiting*, by type. Client-side, off
 * the raw status and type the list endpoint returns. A text send (the post-review mapping on
 * #455): Draft/Invoiced → awaiting payment; Paid → awaiting handoff; SentToVendor → awaiting
 * manual settlement; VendorPaid → awaiting completion; Complete/Cancelled/Failed → done. Voter
 * data has no vendor steps, so once Paid it is awaiting completion (fulfilment).
 */
export function workOrderBucket(
  status: WorkOrderStatus,
  type: WorkOrderType = WorkOrderType.TextSend,
): WorkOrderBucket {
  switch (type) {
    case WorkOrderType.TextSend:
      return textSendBucket(status);
    case WorkOrderType.VoterData:
      return status === WorkOrderStatus.Paid
        ? WorkOrderBucket.AwaitingCompletion
        : textSendBucket(status);
    default:
      return assertNever(type);
  }
}

function textSendBucket(status: WorkOrderStatus): WorkOrderBucket {
  switch (status) {
    case WorkOrderStatus.Draft:
    case WorkOrderStatus.Invoiced:
      return WorkOrderBucket.AwaitingPayment;
    case WorkOrderStatus.Paid:
      return WorkOrderBucket.AwaitingHandoff;
    case WorkOrderStatus.SentToVendor:
      return WorkOrderBucket.AwaitingManualSettlement;
    case WorkOrderStatus.VendorPaid:
      return WorkOrderBucket.AwaitingCompletion;
    case WorkOrderStatus.Complete:
    case WorkOrderStatus.Cancelled:
    case WorkOrderStatus.Failed:
      return WorkOrderBucket.Done;
    default:
      return assertNever(status);
  }
}

/** Human labels for each operator action (jobs-list button copy). */
export const WORK_ORDER_ACTION_LABELS: Record<WorkOrderAction, string> = {
  [WorkOrderAction.MarkVendorPaid]: 'Mark vendor paid',
  [WorkOrderAction.Complete]: 'Mark complete',
  [WorkOrderAction.Cancel]: 'Cancel',
  [WorkOrderAction.Fail]: 'Mark failed',
};

/**
 * The operator-driven transitions the jobs-list surfaces for a given status — a
 * *semantically-appropriate subset* of what the entity guard allows, never a superset
 * (so a button never offers an edge the backend would reject). The Cancelled/Failed
 * split drives the choice: Cancel aborts an UNPAID send (no money moved), so it is
 * surfaced from Draft and an unpaid **Invoiced** send (ADR-068 choice 5, where it also
 * voids the Stripe invoice); Failed is a send that took money/handoff but couldn't
 * complete, so **Fail** is surfaced from the invoiced-and-beyond states — even though the
 * entity permits Fail from any non-terminal state. The tail is MarkVendorPaid (from
 * SentToVendor) → Complete (from VendorPaid); terminal states (Complete/Cancelled/Failed)
 * offer nothing.
 */
export function operatorActionsFor(
  status: WorkOrderStatus,
  type: WorkOrderType = WorkOrderType.TextSend,
): WorkOrderAction[] {
  switch (type) {
    case WorkOrderType.TextSend:
      return textSendOperatorActions(status);
    case WorkOrderType.VoterData:
      return voterDataOperatorActions(status);
    default:
      return assertNever(type);
  }
}

function textSendOperatorActions(status: WorkOrderStatus): WorkOrderAction[] {
  switch (status) {
    case WorkOrderStatus.Draft:
      return [WorkOrderAction.Cancel];
    case WorkOrderStatus.Invoiced:
      return [WorkOrderAction.Cancel, WorkOrderAction.Fail];
    case WorkOrderStatus.Paid:
      return [WorkOrderAction.Fail];
    case WorkOrderStatus.SentToVendor:
      return [WorkOrderAction.MarkVendorPaid, WorkOrderAction.Fail];
    case WorkOrderStatus.VendorPaid:
      return [WorkOrderAction.Complete, WorkOrderAction.Fail];
    case WorkOrderStatus.Complete:
    case WorkOrderStatus.Cancelled:
    case WorkOrderStatus.Failed:
      return [];
    default:
      return assertNever(status);
  }
}

/**
 * Voter data shares the unpaid Cancel and the Fail off-ramp, but has no vendor steps to settle,
 * and a paid order completes through fulfilment (attaching the file) rather than a bare
 * Complete button — so Paid offers only Fail.
 */
function voterDataOperatorActions(status: WorkOrderStatus): WorkOrderAction[] {
  switch (status) {
    case WorkOrderStatus.Draft:
      return [WorkOrderAction.Cancel];
    case WorkOrderStatus.Invoiced:
      return [WorkOrderAction.Cancel, WorkOrderAction.Fail];
    case WorkOrderStatus.Paid:
      return [WorkOrderAction.Fail];
    case WorkOrderStatus.SentToVendor:
    case WorkOrderStatus.VendorPaid:
    case WorkOrderStatus.Complete:
    case WorkOrderStatus.Cancelled:
    case WorkOrderStatus.Failed:
      return [];
    default:
      return assertNever(status);
  }
}

/**
 * The transitions the jobs-list surfaces to a campaign **customer** (a full admin, not a
 * SignalVote operator) on their own order. Customers never drive the operator manual tail
 * (MarkVendorPaid/Complete/Fail); the one self-serve edge they own is cancelling an **unpaid
 * invoiced** order (ADR-068 choice 5), which voids the Stripe invoice server-side. Draft
 * abandonment and every paid/terminal state offer nothing here. The same for every type today;
 * the `type` parameter keeps the matrix keyed by type.
 */
export function customerActionsFor(
  status: WorkOrderStatus,
  type: WorkOrderType = WorkOrderType.TextSend,
): WorkOrderAction[] {
  switch (type) {
    case WorkOrderType.TextSend:
    case WorkOrderType.VoterData:
      return unpaidCancelOnly(status);
    default:
      return assertNever(type);
  }
}

function unpaidCancelOnly(status: WorkOrderStatus): WorkOrderAction[] {
  switch (status) {
    case WorkOrderStatus.Invoiced:
      return [WorkOrderAction.Cancel];
    case WorkOrderStatus.Draft:
    case WorkOrderStatus.Paid:
    case WorkOrderStatus.SentToVendor:
    case WorkOrderStatus.VendorPaid:
    case WorkOrderStatus.Complete:
    case WorkOrderStatus.Cancelled:
    case WorkOrderStatus.Failed:
      return [];
    default:
      return assertNever(status);
  }
}

/**
 * The friendly, customer-facing label for each raw status — what a campaign admin sees on
 * their own jobs, distinct from the operator-facing raw enum name. The internal spine
 * (Paid / SentToVendor / VendorPaid) collapses to a single "Processing" the customer doesn't
 * need to reason about; the terminal off-ramps read as "Cancelled" / "Needs attention".
 */
export const WORK_ORDER_CUSTOMER_STATUS_LABELS: Record<WorkOrderStatus, string> = {
  [WorkOrderStatus.Draft]: 'Draft',
  [WorkOrderStatus.Invoiced]: 'Awaiting payment',
  [WorkOrderStatus.Paid]: 'Processing',
  [WorkOrderStatus.SentToVendor]: 'Processing',
  [WorkOrderStatus.VendorPaid]: 'Processing',
  [WorkOrderStatus.Complete]: 'Sent',
  [WorkOrderStatus.Cancelled]: 'Cancelled',
  [WorkOrderStatus.Failed]: 'Needs attention',
};

/**
 * The friendly customer-facing label for a status, by type. A text send reads as
 * {@link WORK_ORDER_CUSTOMER_STATUS_LABELS}. A paid Voter data order is being prepared (its list is
 * exported from L2 automatically, or by hand when held), and a completed one was delivered, not sent.
 */
export function workOrderCustomerStatusLabel(
  status: WorkOrderStatus,
  type: WorkOrderType = WorkOrderType.TextSend,
): string {
  switch (type) {
    case WorkOrderType.TextSend:
      return WORK_ORDER_CUSTOMER_STATUS_LABELS[status];
    case WorkOrderType.VoterData:
      switch (status) {
        case WorkOrderStatus.Paid:
          return 'Being prepared';
        case WorkOrderStatus.Complete:
          return 'Delivered';
        default:
          return WORK_ORDER_CUSTOMER_STATUS_LABELS[status];
      }
    default:
      return assertNever(type);
  }
}

/**
 * The customer-facing jobs-list grouping — the friendly sibling of {@link WorkOrderBucket}.
 * Where the operator buckets track what a job is *awaiting* along the internal spine, the
 * customer groups collapse the mid-spine mechanics into a single "Processing" and read as
 * plain outcomes: Drafts / Awaiting payment / Processing / Sent / Closed.
 */
export enum WorkOrderCustomerGroup {
  Draft = 'Draft',
  AwaitingPayment = 'AwaitingPayment',
  Processing = 'Processing',
  Sent = 'Sent',
  Closed = 'Closed',
}

/** Human labels for each customer jobs-list group. */
export const WORK_ORDER_CUSTOMER_GROUP_LABELS: Record<WorkOrderCustomerGroup, string> = {
  [WorkOrderCustomerGroup.Draft]: 'Drafts',
  [WorkOrderCustomerGroup.AwaitingPayment]: 'Awaiting payment',
  [WorkOrderCustomerGroup.Processing]: 'Processing',
  [WorkOrderCustomerGroup.Sent]: 'Sent',
  [WorkOrderCustomerGroup.Closed]: 'Closed',
};

/** Render order for the customer jobs-list groups — top-to-bottom as a job progresses. */
export const WORK_ORDER_CUSTOMER_GROUP_ORDER: readonly WorkOrderCustomerGroup[] = [
  WorkOrderCustomerGroup.Draft,
  WorkOrderCustomerGroup.AwaitingPayment,
  WorkOrderCustomerGroup.Processing,
  WorkOrderCustomerGroup.Sent,
  WorkOrderCustomerGroup.Closed,
];

/**
 * Maps a Work Order status to the customer-facing group it belongs in. The internal
 * mid-spine states (Paid / SentToVendor / VendorPaid) all collapse to Processing; the
 * terminal off-ramps (Cancelled / Failed) land in Closed. Every type shares these groups — a
 * completed Voter data order sits in the same finished group as a sent text send, so the
 * filter chips stay the same for every campaign.
 */
export function workOrderCustomerGroup(
  status: WorkOrderStatus,
  type: WorkOrderType = WorkOrderType.TextSend,
): WorkOrderCustomerGroup {
  switch (type) {
    case WorkOrderType.TextSend:
    case WorkOrderType.VoterData:
      return customerGroupOf(status);
    default:
      return assertNever(type);
  }
}

function customerGroupOf(status: WorkOrderStatus): WorkOrderCustomerGroup {
  switch (status) {
    case WorkOrderStatus.Draft:
      return WorkOrderCustomerGroup.Draft;
    case WorkOrderStatus.Invoiced:
      return WorkOrderCustomerGroup.AwaitingPayment;
    case WorkOrderStatus.Paid:
    case WorkOrderStatus.SentToVendor:
    case WorkOrderStatus.VendorPaid:
      return WorkOrderCustomerGroup.Processing;
    case WorkOrderStatus.Complete:
      return WorkOrderCustomerGroup.Sent;
    case WorkOrderStatus.Cancelled:
    case WorkOrderStatus.Failed:
      return WorkOrderCustomerGroup.Closed;
    default:
      return assertNever(status);
  }
}

/**
 * Who a Text send texts, in the words every screen uses: the numbers uploaded, the list bought (still
 * being bought until it is paid for) or the campaign's voters with a cell phone.
 */
export function textSendRecipientsInWords(
  source: RecipientSource,
  count: number | null,
  status: WorkOrderStatus,
): string {
  const n = count === null ? '—' : count.toLocaleString('en-US');
  switch (source) {
    case RecipientSource.UploadedCsv:
      return `${n} numbers you uploaded`;
    case RecipientSource.BoughtList:
      return status === WorkOrderStatus.Draft || status === WorkOrderStatus.Invoiced
        ? `${n} voters on the list you’re buying`
        : `${n} voters on the list you bought`;
    case RecipientSource.CampaignCellPhones:
      return `${n} of your voters with a cell phone`;
    default:
      return assertNever(source);
  }
}

/** A Voter data order's list is importing into the campaign's voters (delivery starts the import). */
export function isVoterDataImporting(importStatus: ImportJobStatus | null | undefined): boolean {
  return importStatus === ImportJobStatus.Pending || importStatus === ImportJobStatus.Running;
}
